// Centralized location data for the entire application
import type { Coordinates } from "@/lib/types";
export type { Coordinates } from "@/lib/types";

export type LocationType = "state" | "lga" | "neighborhood";

export type NigerianLocation = {
  id: string;
  name: string;
  type: LocationType;
  state: string;
  lga?: string;
  coordinates: Coordinates;
  description?: string;
  popular: boolean;
  searchTerms?: string[]; // Additional search terms to help with searching
};

/**
 * Complete list of Nigerian locations with all metadata
 * Currently focused on Lagos and Oyo States with their LGAs and neighborhoods
 *
 * Hierarchy:
 * 1. States (Lagos, Oyo)
 * 2. LGAs within each state
 * 3. Neighborhoods within each LGA
 *
 * Each location includes:
 * - Unique ID
 * - Display name
 * - Type (state/lga/neighborhood)
 * - Geographic info (state, lga if applicable, coordinates)
 * - Description
 * - Popularity flag (for prioritizing in search results)
 * - Search terms (aliases or alternative spellings)
 */
export const nigerianLocations: NigerianLocation[] = [
  // =========================================================================
  // STATES
  // =========================================================================
  {
    id: "lagos_state",
    name: "Lagos",
    type: "state",
    state: "Lagos",
    coordinates: { lat: 6.5244, lng: 3.3792 },
    description:
      "Lagos State is the smallest state in Nigeria, yet it has the highest urban population.",
    popular: true,
    searchTerms: ["lagos state", "eko"],
  },
  {
    id: "oyo_state",
    name: "Oyo",
    type: "state",
    state: "Oyo",
    coordinates: { lat: 7.3775, lng: 3.947 },
    description:
      "Oyo State is located in southwestern Nigeria with Ibadan as its capital and largest city.",
    popular: true,
    searchTerms: ["oyo state", "ibadan"],
  },

  // =========================================================================
  // LAGOS STATE - LOCAL GOVERNMENT AREAS (LGAs)
  // =========================================================================
  {
    id: "eti_osa_lga",
    name: "Eti-Osa",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4589, lng: 3.4246 },
    description:
      "Eti-Osa is a Local Government Area in Lagos State that contains many upscale areas including parts of Lekki and Victoria Island.",
    popular: true,
    searchTerms: ["eti osa", "etiosa"],
  },
  {
    id: "ikeja_lga",
    name: "Ikeja",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.6018, lng: 3.3515 },
    description:
      "Ikeja is the capital of Lagos State and a major commercial center.",
    popular: true,
    searchTerms: ["ikeja lga"],
  },
  {
    id: "surulere_lga",
    name: "Surulere",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.5059, lng: 3.3509 },
    description:
      "Surulere is a residential and commercial Local Government Area in Lagos.",
    popular: true,
    searchTerms: ["surulere lga"],
  },
  {
    id: "kosofe_lga",
    name: "Kosofe",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.5656, lng: 3.3892 },
    description:
      "Kosofe is a Local Government Area in Lagos State that includes areas like Gbagada and Ogudu.",
    popular: false,
    searchTerms: ["kosofe lga"],
  },
  {
    id: "ikorodu_lga",
    name: "Ikorodu",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.6194, lng: 3.5105 },
    description: "Ikorodu is a large Local Government Area in Lagos State.",
    popular: false,
    searchTerms: ["ikorodu lga"],
  },
  {
    id: "alimosho_lga",
    name: "Alimosho",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.5744, lng: 3.2003 },
    description:
      "Alimosho is the largest Local Government Area in Lagos State.",
    popular: false,
    searchTerms: ["alimosho lga"],
  },
  {
    id: "ajeromi_ifelodun_lga",
    name: "Ajeromi-Ifelodun",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.455, lng: 3.3235 },
    description:
      "Ajeromi-Ifelodun is a Local Government Area in Lagos State that includes Ajegunle.",
    popular: false,
    searchTerms: ["ajeromi", "ajeromi ifelodun", "ajeromi-ifelodun lga"],
  },
  {
    id: "apapa_lga",
    name: "Apapa",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4463, lng: 3.364 },
    description: "Apapa is a major port Local Government Area in Lagos State.",
    popular: false,
    searchTerms: ["apapa lga"],
  },
  {
    id: "shomolu_lga",
    name: "Shomolu",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.541, lng: 3.384 },
    description: "Shomolu is a Local Government Area in Lagos State.",
    popular: false,
    searchTerms: ["shomolu lga"],
  },

  // Additional Lagos LGAs that were missing
  {
    id: "agege_lga",
    name: "Agege",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.6177, lng: 3.323 },
    description:
      "Agege is a Local Government Area in Lagos State with a major food market and residential areas.",
    popular: false,
    searchTerms: ["agege lga"],
  },
  {
    id: "amuwo_odofin_lga",
    name: "Amuwo-Odofin",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4293, lng: 3.2701 },
    description:
      "Amuwo-Odofin is a Local Government Area in Lagos State that includes Festac Town and other residential areas.",
    popular: false,
    searchTerms: ["amuwo odofin lga", "amuwo-odofin"],
  },
  {
    id: "badagry_lga",
    name: "Badagry",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4154, lng: 2.8874 },
    description:
      "Badagry is a coastal Local Government Area in Lagos State with historical significance in the trans-Atlantic slave trade.",
    popular: false,
    searchTerms: ["badagry lga"],
  },
  {
    id: "epe_lga",
    name: "Epe",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.6004, lng: 3.9839 },
    description:
      "Epe is a Local Government Area in Lagos State known for fishing and agriculture.",
    popular: false,
    searchTerms: ["epe lga"],
  },
  {
    id: "ibeju_lekki_lga",
    name: "Ibeju-Lekki",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4698, lng: 3.7184 },
    description:
      "Ibeju-Lekki is a rapidly developing Local Government Area in Lagos State with beaches and the Lekki Free Trade Zone.",
    popular: false,
    searchTerms: ["ibeju lekki lga", "ibeju-lekki"],
  },
  {
    id: "ifako_ijaiye_lga",
    name: "Ifako-Ijaiye",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.6864, lng: 3.3002 },
    description:
      "Ifako-Ijaiye is a Local Government Area in Lagos State located in the northern part of the state.",
    popular: false,
    searchTerms: ["ifako ijaiye lga", "ifako-ijaiye"],
  },
  {
    id: "lagos_island_lga",
    name: "Lagos Island",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.455, lng: 3.405 },
    description:
      "Lagos Island is a Local Government Area in Lagos State that includes the central business district and traditional markets.",
    popular: true,
    searchTerms: ["lagos island lga"],
  },
  {
    id: "lagos_mainland_lga",
    name: "Lagos Mainland",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.508, lng: 3.3702 },
    description:
      "Lagos Mainland is a Local Government Area in Lagos State that includes Yaba and other commercial and residential areas.",
    popular: true,
    searchTerms: ["lagos mainland lga"],
  },
  {
    id: "mushin_lga",
    name: "Mushin",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.5352, lng: 3.3427 },
    description:
      "Mushin is a densely populated Local Government Area in Lagos State with various markets and residential areas.",
    popular: false,
    searchTerms: ["mushin lga"],
  },
  {
    id: "ojo_lga",
    name: "Ojo",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.4575, lng: 3.1829 },
    description:
      "Ojo is a Local Government Area in Lagos State that houses the Lagos State University and military barracks.",
    popular: false,
    searchTerms: ["ojo lga"],
  },
  {
    id: "oshodi_isolo_lga",
    name: "Oshodi-Isolo",
    type: "lga",
    state: "Lagos",
    coordinates: { lat: 6.5345, lng: 3.3198 },
    description:
      "Oshodi-Isolo is a Local Government Area in Lagos State with a major transport hub and market.",
    popular: false,
    searchTerms: ["oshodi isolo lga", "oshodi-isolo"],
  },

  // =========================================================================
  // OYO STATE - LOCAL GOVERNMENT AREAS (LGAs)
  // =========================================================================
  {
    id: "afijio_lga",
    name: "Afijio",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.8339, lng: 3.9226 },
    description: "Afijio is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["afijio lga"],
  },
  {
    id: "akinyele_lga",
    name: "Akinyele",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.5498, lng: 3.9144 },
    description: "Akinyele is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["akinyele lga"],
  },
  {
    id: "atiba_lga",
    name: "Atiba",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.8528, lng: 3.9387 },
    description: "Atiba is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["atiba lga"],
  },
  {
    id: "atisbo_lga",
    name: "Atisbo",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.2361, lng: 3.4031 },
    description: "Atisbo is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["atisbo lga"],
  },
  {
    id: "egbeda_lga",
    name: "Egbeda",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.3789, lng: 4.0547 },
    description: "Egbeda is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["egbeda lga"],
  },
  {
    id: "ibadan_north_lga",
    name: "Ibadan North",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.4102, lng: 3.9165 },
    description: "Ibadan North is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ibadan north lga"],
  },
  {
    id: "ibadan_north_east_lga",
    name: "Ibadan North East",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.4105, lng: 3.9359 },
    description: "Ibadan North East is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ibadan north east lga"],
  },
  {
    id: "ibadan_north_west_lga",
    name: "Ibadan North West",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.3956, lng: 3.8775 },
    description: "Ibadan North West is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ibadan north west lga"],
  },
  {
    id: "ibadan_south_east_lga",
    name: "Ibadan South East",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.3578, lng: 3.9344 },
    description: "Ibadan South East is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ibadan south east lga"],
  },
  {
    id: "ibadan_south_west_lga",
    name: "Ibadan South West",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.3504, lng: 3.8775 },
    description: "Ibadan South West is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ibadan south west lga"],
  },
  {
    id: "ibarapa_central_lga",
    name: "Ibarapa Central",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.4413, lng: 3.3837 },
    description: "Ibarapa Central is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ibarapa central lga"],
  },
  {
    id: "ibarapa_east_lga",
    name: "Ibarapa East",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.4413, lng: 3.4837 },
    description: "Ibarapa East is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ibarapa east lga"],
  },
  {
    id: "ibarapa_north_lga",
    name: "Ibarapa North",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.5413, lng: 3.3837 },
    description: "Ibarapa North is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ibarapa north lga"],
  },
  {
    id: "ido_lga",
    name: "Ido",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.4822, lng: 3.7154 },
    description: "Ido is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ido lga"],
  },
  {
    id: "irepo_lga",
    name: "Irepo",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 9.0073, lng: 3.8342 },
    description: "Irepo is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["irepo lga"],
  },
  {
    id: "iseyin_lga",
    name: "Iseyin",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.9667, lng: 3.6 },
    description: "Iseyin is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["iseyin lga"],
  },
  {
    id: "itesiwaju_lga",
    name: "Itesiwaju",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.2625, lng: 3.5842 },
    description: "Itesiwaju is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["itesiwaju lga"],
  },
  {
    id: "iwajowa_lga",
    name: "Iwajowa",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.2164, lng: 3.0486 },
    description: "Iwajowa is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["iwajowa lga"],
  },
  {
    id: "kajola_lga",
    name: "Kajola",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.0486, lng: 3.3017 },
    description: "Kajola is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["kajola lga"],
  },
  {
    id: "lagelu_lga",
    name: "Lagelu",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.5434, lng: 4.0547 },
    description: "Lagelu is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["lagelu lga"],
  },
  {
    id: "ogbomosho_north_lga",
    name: "Ogbomosho North",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.1336, lng: 4.2564 },
    description: "Ogbomosho North is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ogbomosho north lga"],
  },
  {
    id: "ogbomosho_south_lga",
    name: "Ogbomosho South",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.0836, lng: 4.2564 },
    description: "Ogbomosho South is a Local Government Area in Oyo State.",
    popular: true,
    searchTerms: ["ogbomosho south lga"],
  },
  {
    id: "ogo_oluwa_lga",
    name: "Ogo Oluwa",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.9747, lng: 4.2199 },
    description: "Ogo Oluwa is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ogo oluwa lga"],
  },
  {
    id: "olorunsogo_lga",
    name: "Olorunsogo",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.938, lng: 3.9411 },
    description: "Olorunsogo is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["olorunsogo lga"],
  },
  {
    id: "oluyole_lga",
    name: "Oluyole",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.2997, lng: 3.8776 },
    description: "Oluyole is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["oluyole lga"],
  },
  {
    id: "ona_ara_lga",
    name: "Ona Ara",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.3333, lng: 4.05 },
    description: "Ona Ara is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ona ara lga"],
  },
  {
    id: "orelope_lga",
    name: "Orelope",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.8079, lng: 3.3483 },
    description: "Orelope is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["orelope lga"],
  },
  {
    id: "ori_ire_lga",
    name: "Ori Ire",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.4027, lng: 3.9957 },
    description: "Ori Ire is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["ori ire lga"],
  },
  {
    id: "oyo_east_lga",
    name: "Oyo East",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.8372, lng: 3.9559 },
    description: "Oyo East is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["oyo east lga"],
  },
  {
    id: "oyo_west_lga",
    name: "Oyo West",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 7.8372, lng: 3.9059 },
    description: "Oyo West is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["oyo west lga"],
  },
  {
    id: "saki_east_lga",
    name: "Saki East",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.6714, lng: 3.3939 },
    description: "Saki East is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["saki east lga"],
  },
  {
    id: "saki_west_lga",
    name: "Saki West",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.6714, lng: 3.3439 },
    description: "Saki West is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["saki west lga"],
  },
  {
    id: "surulere_oyo_lga",
    name: "Surulere",
    type: "lga",
    state: "Oyo",
    coordinates: { lat: 8.0832, lng: 4.1706 },
    description: "Surulere is a Local Government Area in Oyo State.",
    popular: false,
    searchTerms: ["surulere oyo lga"],
  },

  // =========================================================================
  // LAGOS STATE - NEIGHBORHOODS BY LGA
  // =========================================================================

  // ETI-OSA LGA NEIGHBORHOODS
  // Note: Neighborhoods in the high-end Lekki/VI/Ikoyi areas
  {
    id: "lekki_phase_1",
    name: "Lekki Phase 1",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4478, lng: 3.4723 },
    description:
      "Upscale residential area with beaches, shopping centers, and the Lekki Conservation Centre.",
    popular: true,
    searchTerms: [
      "lekki phase 1",
      "lekki phase one",
      "lekki 1",
      "lekki peninsula phase 1",
    ],
  },
  {
    id: "lekki_phase_2",
    name: "Lekki Phase 2",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4698, lng: 3.5852 },
    description: "Developing residential area adjacent to Lekki Phase 1.",
    popular: false,
    searchTerms: ["lekki phase 2", "lekki phase two", "lekki 2"],
  },
  {
    id: "victoria_island",
    name: "Victoria Island",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4281, lng: 3.4219 },
    description:
      "Business and financial district with high-end shops, restaurants, and office complexes.",
    popular: true,
    searchTerms: ["vi", "victoria island lagos"],
  },
  {
    id: "ikoyi",
    name: "Ikoyi",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4547, lng: 3.4344 },
    description:
      "Affluent neighborhood with embassies, luxury apartments, and recreational facilities.",
    popular: true,
    searchTerms: ["ikoyi lagos"],
  },
  {
    id: "ajah",
    name: "Ajah",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4683, lng: 3.5655 },
    description:
      "Developing residential area with gated communities and shopping centers.",
    popular: true,
    searchTerms: ["ajah lagos"],
  },
  {
    id: "sangotedo",
    name: "Sangotedo",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.464, lng: 3.6326 },
    description: "Fast-growing residential area in the Lekki-Epe corridor.",
    popular: false,
    searchTerms: ["sangotedo lagos"],
  },
  {
    id: "chevron",
    name: "Chevron",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4498, lng: 3.5307 },
    description: "Residential area named after the nearby Chevron facility.",
    popular: false,
    searchTerms: ["chevron lekki", "chevron drive"],
  },
  {
    id: "ikate_elegushi",
    name: "Ikate Elegushi",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4398, lng: 3.489 },
    description:
      "Residential area near Lekki Phase 1 with a mix of apartments and houses.",
    popular: false,
    searchTerms: ["ikate", "elegushi", "ikate elegushi"],
  },
  {
    id: "osapa_london",
    name: "Osapa London",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4429, lng: 3.5148 },
    description: "Residential area near Lekki Phase 1.",
    popular: false,
    searchTerms: ["osapa", "osapa london"],
  },
  {
    id: "banana_island",
    name: "Banana Island",
    type: "neighborhood",
    state: "Lagos",
    lga: "Eti-Osa",
    coordinates: { lat: 6.4392, lng: 3.4263 },
    description:
      "One of the most expensive and exclusive neighborhoods in Lagos.",
    popular: false,
    searchTerms: ["banana island"],
  },

  // IKEJA LGA NEIGHBORHOODS
  // Note: Main commercial district and Lagos state capital
  {
    id: "ikeja_gra",
    name: "Ikeja GRA",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikeja",
    coordinates: { lat: 6.5821, lng: 3.3515 },
    description:
      "Government Reserved Area in Ikeja, an upscale residential district.",
    popular: false,
    searchTerms: ["ikeja gra", "government reserved area ikeja"],
  },
  {
    id: "allen_avenue",
    name: "Allen Avenue",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikeja",
    coordinates: { lat: 6.6018, lng: 3.3515 },
    description:
      "Major commercial street in Ikeja known for shopping and nightlife.",
    popular: false,
    searchTerms: ["allen", "allen avenue"],
  },
  {
    id: "maryland",
    name: "Maryland",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikeja",
    coordinates: { lat: 6.5764, lng: 3.3653 },
    description:
      "Bustling commercial and residential area in Lagos known for its vibrant markets and transport hub.",
    popular: true,
    searchTerms: ["maryland lagos", "maryland ikeja"],
  },
  {
    id: "computer_village",
    name: "Computer Village",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikeja",
    coordinates: { lat: 6.601, lng: 3.3428 },
    description: "Nigeria's largest technology market.",
    popular: false,
    searchTerms: ["computer village", "ikeja computer village"],
  },
  {
    id: "oregun",
    name: "Oregun",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikeja",
    coordinates: { lat: 6.6047, lng: 3.3594 },
    description: "Industrial area in Ikeja.",
    popular: false,
    searchTerms: ["oregun"],
  },

  // SURULERE LGA NEIGHBORHOODS
  // Note: Predominantly residential area
  {
    id: "surulere_central",
    name: "Surulere",
    type: "neighborhood",
    state: "Lagos",
    lga: "Surulere",
    coordinates: { lat: 6.5059, lng: 3.3509 },
    description:
      "Residential district with the National Stadium and shopping centers.",
    popular: true,
    searchTerms: ["surulere lagos", "surulere central"],
  },
  {
    id: "adeniran_ogunsanya",
    name: "Adeniran Ogunsanya",
    type: "neighborhood",
    state: "Lagos",
    lga: "Surulere",
    coordinates: { lat: 6.4952, lng: 3.3565 },
    description: "Commercial area in Surulere with a major shopping mall.",
    popular: false,
    searchTerms: ["adeniran ogunsanya", "adeniran"],
  },
  {
    id: "aguda",
    name: "Aguda",
    type: "neighborhood",
    state: "Lagos",
    lga: "Surulere",
    coordinates: { lat: 6.49, lng: 3.3414 },
    description: "Residential area in Surulere.",
    popular: false,
    searchTerms: ["aguda surulere"],
  },
  {
    id: "alaka",
    name: "Alaka",
    type: "neighborhood",
    state: "Lagos",
    lga: "Surulere",
    coordinates: { lat: 6.491, lng: 3.3632 },
    description: "Area in Surulere near the National Stadium.",
    popular: false,
    searchTerms: ["alaka estate", "alaka surulere"],
  },

  // KOSOFE LGA NEIGHBORHOODS
  // Note: Includes many mainland commercial and residential areas
  {
    id: "gbagada",
    name: "Gbagada",
    type: "neighborhood",
    state: "Lagos",
    lga: "Kosofe",
    coordinates: { lat: 6.5584, lng: 3.3915 },
    description: "Residential area with good access to major parts of Lagos.",
    popular: true,
    searchTerms: ["gbagada", "gbagada kosofe"],
  },
  {
    id: "ogudu",
    name: "Ogudu",
    type: "neighborhood",
    state: "Lagos",
    lga: "Kosofe",
    coordinates: { lat: 6.5777, lng: 3.3885 },
    description: "Residential area in Kosofe LGA.",
    popular: false,
    searchTerms: ["ogudu lagos", "ogudu gra"],
  },
  {
    id: "ojota",
    name: "Ojota",
    type: "neighborhood",
    state: "Lagos",
    lga: "Kosofe",
    coordinates: { lat: 6.5873, lng: 3.3786 },
    description: "Major transport hub in Lagos with a large bus terminal.",
    popular: false,
    searchTerms: ["ojota lagos"],
  },
  {
    id: "ketu",
    name: "Ketu",
    type: "neighborhood",
    state: "Lagos",
    lga: "Kosofe",
    coordinates: { lat: 6.5999, lng: 3.3831 },
    description: "Bustling commercial and residential area.",
    popular: false,
    searchTerms: ["ketu lagos"],
  },
  {
    id: "mile_12",
    name: "Mile 12",
    type: "neighborhood",
    state: "Lagos",
    lga: "Kosofe",
    coordinates: { lat: 6.6015, lng: 3.3969 },
    description: "Area known for its large food market.",
    popular: false,
    searchTerms: ["mile 12", "mile twelve"],
  },

  // LAGOS ISLAND LGA NEIGHBORHOODS
  // Note: The commercial heart of Lagos with major financial institutions
  {
    id: "marina",
    name: "Marina",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Island",
    coordinates: { lat: 6.4543, lng: 3.3841 },
    description:
      "Business district with major banks and corporate headquarters.",
    popular: true,
    searchTerms: ["marina lagos", "marina island"],
  },
  {
    id: "tinubu_square",
    name: "Tinubu Square",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Island",
    coordinates: { lat: 6.4532, lng: 3.3847 },
    description: "Historical area and commercial center on Lagos Island.",
    popular: false,
    searchTerms: ["tinubu square", "tinubu lagos"],
  },
  {
    id: "broad_street",
    name: "Broad Street",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Island",
    coordinates: { lat: 6.4537, lng: 3.385 },
    description: "Major commercial street with historical significance.",
    popular: true,
    searchTerms: ["broad street lagos", "broad street"],
  },

  // LAGOS MAINLAND LGA NEIGHBORHOODS
  // Note: Traditional commercial and educational center
  {
    id: "oyingbo",
    name: "Oyingbo",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Mainland",
    coordinates: { lat: 6.4882, lng: 3.3789 },
    description: "Major market area and transport hub.",
    popular: true,
    searchTerms: ["oyingbo", "oyingbo market"],
  },
  {
    id: "sabo_yaba",
    name: "Sabo",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Mainland",
    coordinates: { lat: 6.5144, lng: 3.3762 },
    description: "Commercial and residential area in Yaba.",
    popular: false,
    searchTerms: ["sabo yaba", "sabo"],
  },
  {
    id: "tejuosho",
    name: "Tejuosho",
    type: "neighborhood",
    state: "Lagos",
    lga: "Lagos Mainland",
    coordinates: { lat: 6.5097, lng: 3.3669 },
    description: "Known for the famous Tejuosho Market.",
    popular: true,
    searchTerms: ["tejuosho", "tejuosho market"],
  },

  // OTHER LAGOS LGA NEIGHBORHOODS
  {
    id: "yaba",
    name: "Yaba",
    type: "neighborhood",
    state: "Lagos",
    lga: "Mainland",
    coordinates: { lat: 6.5095, lng: 3.3711 },
    description:
      "Educational and technological hub with universities and the Yaba Tech Hub.",
    popular: true,
    searchTerms: ["yaba lagos", "yaba tech"],
  },
  {
    id: "ikorodu_central",
    name: "Ikorodu Central",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ikorodu",
    coordinates: { lat: 6.6194, lng: 3.5105 },
    description:
      "Main commercial hub of Ikorodu area, with growing residential developments.",
    popular: false,
    searchTerms: ["ikorodu central", "ikorodu town"],
  },
  {
    id: "ajegunle",
    name: "Ajegunle",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ajeromi-Ifelodun",
    coordinates: { lat: 6.455, lng: 3.3235 },
    description:
      "Densely populated area known for producing music and football talents.",
    popular: false,
    searchTerms: ["ajegunle", "aj city", "ajegunle ajeromi"],
  },
  {
    id: "apapa_central",
    name: "Apapa Central",
    type: "neighborhood",
    state: "Lagos",
    lga: "Apapa",
    coordinates: { lat: 6.4463, lng: 3.364 },
    description:
      "Major port area with shipping activities and container terminals.",
    popular: false,
    searchTerms: ["apapa wharf", "apapa port", "apapa central"],
  },
  {
    id: "tin_can_island",
    name: "Tin Can Island",
    type: "neighborhood",
    state: "Lagos",
    lga: "Apapa",
    coordinates: { lat: 6.439, lng: 3.352 },
    description: "Port area with container terminals and customs operations.",
    popular: true,
    searchTerms: ["tin can", "tin can port"],
  },
  {
    id: "pedro",
    name: "Pedro",
    type: "neighborhood",
    state: "Lagos",
    lga: "Shomolu",
    coordinates: { lat: 6.541, lng: 3.384 },
    description: "Residential area in Shomolu LGA.",
    popular: false,
    searchTerms: ["pedro road", "pedro shomolu"],
  },
  {
    id: "bariga",
    name: "Bariga",
    type: "neighborhood",
    state: "Lagos",
    lga: "Shomolu",
    coordinates: { lat: 6.532, lng: 3.383 },
    description: "Residential area with mixed development.",
    popular: false,
    searchTerms: ["bariga lagos"],
  },
  {
    id: "fola_agoro",
    name: "Fola Agoro",
    type: "neighborhood",
    state: "Lagos",
    lga: "Shomolu",
    coordinates: { lat: 6.5396, lng: 3.3792 },
    description: "Residential neighborhood in Shomolu.",
    popular: false,
    searchTerms: ["fola agoro"],
  },

  // OSHODI-ISOLO LGA NEIGHBORHOODS
  // Note: Commercial area with markets and transport hubs
  {
    id: "ajao_estate",
    name: "Ajao Estate",
    type: "neighborhood",
    state: "Lagos",
    lga: "Oshodi-Isolo",
    coordinates: { lat: 6.5491, lng: 3.3372 },
    description:
      "Residential and commercial district near the International Airport with mixed development.",
    popular: true,
    searchTerms: ["ajao estate", "ajao", "airport road"],
  },

  // MUSHIN LGA NEIGHBORHOODS
  // Note: Dense urban area with markets and commercial activity
  {
    id: "palm_avenue",
    name: "Palm Avenue",
    type: "neighborhood",
    state: "Lagos",
    lga: "Mushin",
    coordinates: { lat: 6.5352, lng: 3.3427 },
    description: "Residential and commercial area in Mushin.",
    popular: false,
    searchTerms: ["palm avenue mushin"],
  },
  {
    id: "ladipo_mushin",
    name: "Ladipo",
    type: "neighborhood",
    state: "Lagos",
    lga: "Mushin",
    coordinates: { lat: 6.537, lng: 3.345 },
    description: "Famous for the Ladipo Auto Spare Parts Market.",
    popular: true,
    searchTerms: ["ladipo", "ladipo market", "ladipo mushin"],
  },

  // IFAKO-IJAIYE LGA NEIGHBORHOODS
  // Note: Expanding residential area in mainland Lagos
  {
    id: "ifako_ijaiye_central",
    name: "Ifako",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ifako-Ijaiye",
    coordinates: { lat: 6.6864, lng: 3.3002 },
    description: "Residential district in Ifako-Ijaiye.",
    popular: false,
    searchTerms: ["ifako ijaiye central", "ifako central"],
  },
  {
    id: "obawole",
    name: "Obawole",
    type: "neighborhood",
    state: "Lagos",
    lga: "Ifako-Ijaiye",
    coordinates: { lat: 6.6782, lng: 3.3045 },
    description: "Residential area in Ifako-Ijaiye.",
    popular: false,
    searchTerms: ["obawole ifako"],
  },

  // AGEGE LGA NEIGHBORHOODS
  // Note: Densely populated area with markets and small businesses
  {
    id: "pen_cinema",
    name: "Pen Cinema",
    type: "neighborhood",
    state: "Lagos",
    lga: "Agege",
    coordinates: { lat: 6.6177, lng: 3.323 },
    description: "Commercial hub with the iconic Pen Cinema Bridge.",
    popular: true,
    searchTerms: ["pen cinema agege"],
  },

  // =========================================================================
  // OYO STATE - NEIGHBORHOODS BY LGA
  // =========================================================================

  // IBADAN NORTH LGA NEIGHBORHOODS
  {
    id: "bodija",
    name: "Bodija",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North",
    coordinates: { lat: 7.4391, lng: 3.9154 },
    description:
      "Upscale residential area with shopping centers and the Bodija Market.",
    popular: true,
    searchTerms: ["bodija", "bodija estate", "bodija ibadan"],
  },
  {
    id: "agbowo",
    name: "Agbowo",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North",
    coordinates: { lat: 7.4482, lng: 3.9094 },
    description:
      "Area near the University of Ibadan with student housing and commercial activities.",
    popular: false,
    searchTerms: ["agbowo", "agbowo ui", "agbowo ibadan"],
  },
  {
    id: "samonda",
    name: "Samonda",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North",
    coordinates: { lat: 7.4467, lng: 3.9001 },
    description:
      "Residential area with government buildings and upscale residences.",
    popular: false,
    searchTerms: ["samonda", "samonda gra", "samonda ibadan"],
  },
  {
    id: "sango",
    name: "Sango",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North",
    coordinates: { lat: 7.456, lng: 3.9218 },
    description: "Bustling commercial area near the Polytechnic of Ibadan.",
    popular: true,
    searchTerms: ["sango", "sango ibadan"],
  },
  {
    id: "bashorun",
    name: "Bashorun",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North",
    coordinates: { lat: 7.412, lng: 3.9283 },
    description:
      "Residential and commercial area with the popular Jericho Mall.",
    popular: false,
    searchTerms: ["bashorun", "bashorun ibadan"],
  },

  // IBADAN NORTH EAST LGA NEIGHBORHOODS
  {
    id: "iwo_road",
    name: "Iwo Road",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North East",
    coordinates: { lat: 7.4069, lng: 3.9507 },
    description: "Major transport hub and commercial center in Ibadan.",
    popular: true,
    searchTerms: ["iwo road", "iwo road junction"],
  },
  {
    id: "agodi",
    name: "Agodi",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North East",
    coordinates: { lat: 7.4075, lng: 3.9246 },
    description:
      "Government administrative area with the Oyo State Secretariat and Agodi Gardens.",
    popular: false,
    searchTerms: ["agodi", "agodi gra", "agodi gardens"],
  },
  {
    id: "gate",
    name: "Gate",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North East",
    coordinates: { lat: 7.3947, lng: 3.9305 },
    description: "Commercial hub with the popular Gbagi Market.",
    popular: false,
    searchTerms: ["gate ibadan", "gate area"],
  },

  // IBADAN NORTH WEST LGA NEIGHBORHOODS
  {
    id: "oke_ado",
    name: "Oke Ado",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North West",
    coordinates: { lat: 7.3907, lng: 3.878 },
    description: "Central residential and commercial district.",
    popular: false,
    searchTerms: ["oke ado", "oke-ado"],
  },
  {
    id: "dugbe",
    name: "Dugbe",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North West",
    coordinates: { lat: 7.3919, lng: 3.8903 },
    description:
      "Central business district with markets and commercial buildings.",
    popular: true,
    searchTerms: ["dugbe", "dugbe ibadan"],
  },
  {
    id: "onireke",
    name: "Onireke",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan North West",
    coordinates: { lat: 7.4, lng: 3.8713 },
    description: "Upscale residential area with government reserved houses.",
    popular: false,
    searchTerms: ["onireke", "onireke gra"],
  },

  // IBADAN SOUTH EAST LGA NEIGHBORHOODS
  {
    id: "mapo",
    name: "Mapo",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South East",
    coordinates: { lat: 7.3788, lng: 3.929 },
    description:
      "Historical district with the iconic Mapo Hall and traditional Ibadan architecture.",
    popular: true,
    searchTerms: ["mapo", "mapo hill", "mapo hall"],
  },
  {
    id: "orita_aperin",
    name: "Orita Aperin",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South East",
    coordinates: { lat: 7.3649, lng: 3.9371 },
    description: "Residential area with local markets.",
    popular: false,
    searchTerms: ["orita aperin", "aperin"],
  },
  {
    id: "idi_arere",
    name: "Idi Arere",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South East",
    coordinates: { lat: 7.37, lng: 3.9246 },
    description: "Traditional core area of old Ibadan.",
    popular: false,
    searchTerms: ["idi arere", "idiarere"],
  },

  // IBADAN SOUTH WEST LGA NEIGHBORHOODS
  {
    id: "ring_road",
    name: "Ring Road",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South West",
    coordinates: { lat: 7.352, lng: 3.8877 },
    description:
      "Commercial corridor with banks, offices, and shopping centers.",
    popular: true,
    searchTerms: ["ring road", "ringroad"],
  },
  {
    id: "challenge",
    name: "Challenge",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South West",
    coordinates: { lat: 7.3474, lng: 3.8689 },
    description: "Commercial and residential area with numerous businesses.",
    popular: true,
    searchTerms: ["challenge", "challenge ibadan"],
  },
  {
    id: "oluyole_estate",
    name: "Oluyole Estate",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ibadan South West",
    coordinates: { lat: 7.3316, lng: 3.8742 },
    description:
      "Planned residential estate with middle and upper-class housing.",
    popular: false,
    searchTerms: ["oluyole estate", "oluyole"],
  },

  // AKINYELE LGA NEIGHBORHOODS
  {
    id: "moniya",
    name: "Moniya",
    type: "neighborhood",
    state: "Oyo",
    lga: "Akinyele",
    coordinates: { lat: 7.5726, lng: 3.9218 },
    description: "Rural-urban settlement with the railway terminus.",
    popular: false,
    searchTerms: ["moniya", "moniya ibadan"],
  },
  {
    id: "ojoo",
    name: "Ojoo",
    type: "neighborhood",
    state: "Oyo",
    lga: "Akinyele",
    coordinates: { lat: 7.4797, lng: 3.9099 },
    description:
      "Commercial area with the main Ibadan intercity transport terminal.",
    popular: true,
    searchTerms: ["ojoo", "ojoo ibadan"],
  },

  // EGBEDA LGA NEIGHBORHOODS
  {
    id: "egbeda",
    name: "Egbeda",
    type: "neighborhood",
    state: "Oyo",
    lga: "Egbeda",
    coordinates: { lat: 7.3789, lng: 4.0547 },
    description: "Town center and administrative headquarters of Egbeda LGA.",
    popular: false,
    searchTerms: ["egbeda town", "egbeda ibadan"],
  },
  {
    id: "alakia",
    name: "Alakia",
    type: "neighborhood",
    state: "Oyo",
    lga: "Egbeda",
    coordinates: { lat: 7.3993, lng: 4.0375 },
    description: "Industrial area housing the Ibadan Airport.",
    popular: false,
    searchTerms: ["alakia", "alakia ibadan", "airport area"],
  },

  // OLUYOLE LGA NEIGHBORHOODS
  {
    id: "idi_ayunre",
    name: "Idi Ayunre",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oluyole",
    coordinates: { lat: 7.2423, lng: 3.8713 },
    description: "Headquarters of Oluyole LGA with rural settlements.",
    popular: false,
    searchTerms: ["idi ayunre", "idiayunre"],
  },
  {
    id: "new_garage",
    name: "New Garage",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oluyole",
    coordinates: { lat: 7.3172, lng: 3.8612 },
    description: "Commercial area with transport terminals and markets.",
    popular: false,
    searchTerms: ["new garage", "new garage ibadan"],
  },

  // LAGELU LGA NEIGHBORHOODS
  {
    id: "iyana_offa",
    name: "Iyana Offa",
    type: "neighborhood",
    state: "Oyo",
    lga: "Lagelu",
    coordinates: { lat: 7.5265, lng: 4.0535 },
    description: "Commercial junction in Lagelu LGA.",
    popular: false,
    searchTerms: ["iyana offa", "iyanaoffa"],
  },
  {
    id: "lalupon",
    name: "Lalupon",
    type: "neighborhood",
    state: "Oyo",
    lga: "Lagelu",
    coordinates: { lat: 7.5121, lng: 4.0892 },
    description: "Administrative headquarters of Lagelu LGA.",
    popular: false,
    searchTerms: ["lalupon", "lalupon ibadan"],
  },

  // OGBOMOSHO NORTH LGA NEIGHBORHOODS
  {
    id: "ogbomosho_central",
    name: "Ogbomosho Central",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ogbomosho North",
    coordinates: { lat: 8.1336, lng: 4.2564 },
    description:
      "City center with commercial activities and the palace of the Soun of Ogbomosho.",
    popular: true,
    searchTerms: ["ogbomosho central", "ogbomosho town"],
  },
  {
    id: "takie",
    name: "Takie",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ogbomosho North",
    coordinates: { lat: 8.1395, lng: 4.2448 },
    description: "Commercial area in Ogbomosho with markets and businesses.",
    popular: false,
    searchTerms: ["takie", "takie ogbomosho"],
  },

  // OGBOMOSHO SOUTH LGA NEIGHBORHOODS
  {
    id: "arowomole",
    name: "Arowomole",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ogbomosho South",
    coordinates: { lat: 8.1022, lng: 4.2564 },
    description: "Residential and commercial area in southern Ogbomosho.",
    popular: false,
    searchTerms: ["arowomole", "arowomole ogbomosho"],
  },
  {
    id: "iresaadu",
    name: "Iresaadu",
    type: "neighborhood",
    state: "Oyo",
    lga: "Ogbomosho South",
    coordinates: { lat: 8.0761, lng: 4.2512 },
    description: "Residential district in Ogbomosho South.",
    popular: false,
    searchTerms: ["iresaadu", "iresa adu"],
  },

  // OYO EAST LGA NEIGHBORHOODS
  {
    id: "bara",
    name: "Bara",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oyo East",
    coordinates: { lat: 7.8392, lng: 3.9459 },
    description: "Historical area in Oyo town.",
    popular: false,
    searchTerms: ["bara", "bara oyo"],
  },
  {
    id: "aganju",
    name: "Aganju",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oyo East",
    coordinates: { lat: 7.8419, lng: 3.9559 },
    description: "Residential neighborhood in Oyo East.",
    popular: false,
    searchTerms: ["aganju", "aganju oyo"],
  },

  // OYO WEST LGA NEIGHBORHOODS
  {
    id: "awe",
    name: "Awe",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oyo West",
    coordinates: { lat: 7.8372, lng: 3.8865 },
    description: "Town area in Oyo West LGA.",
    popular: false,
    searchTerms: ["awe", "awe town"],
  },
  {
    id: "isokun",
    name: "Isokun",
    type: "neighborhood",
    state: "Oyo",
    lga: "Oyo West",
    coordinates: { lat: 7.8372, lng: 3.9059 },
    description: "Residential district in Oyo West.",
    popular: false,
    searchTerms: ["isokun", "isokun oyo"],
  },

  // ISEYIN LGA NEIGHBORHOODS
  {
    id: "iseyin_central",
    name: "Iseyin Central",
    type: "neighborhood",
    state: "Oyo",
    lga: "Iseyin",
    coordinates: { lat: 7.9667, lng: 3.6 },
    description:
      "City center of Iseyin, known for traditional weaving and crafts.",
    popular: true,
    searchTerms: ["iseyin central", "iseyin town"],
  },
  {
    id: "koso",
    name: "Koso",
    type: "neighborhood",
    state: "Oyo",
    lga: "Iseyin",
    coordinates: { lat: 7.972, lng: 3.6092 },
    description: "Residential area in Iseyin.",
    popular: false,
    searchTerms: ["koso", "koso iseyin"],
  },

  // SAKI WEST LGA NEIGHBORHOODS
  {
    id: "saki_town",
    name: "Saki Town",
    type: "neighborhood",
    state: "Oyo",
    lga: "Saki West",
    coordinates: { lat: 8.6714, lng: 3.3439 },
    description:
      "Main urban center of Saki, a border town in northern Oyo State.",
    popular: true,
    searchTerms: ["saki town", "saki central"],
  },
  {
    id: "ago_are",
    name: "Ago Are",
    type: "neighborhood",
    state: "Oyo",
    lga: "Saki West",
    coordinates: { lat: 8.6514, lng: 3.3239 },
    description: "Residential community in Saki West.",
    popular: false,
    searchTerms: ["ago are", "agoare"],
  },
];

// Utility functions for location data

/**
 * Search locations by query string
 * Searches through name, description, and searchTerms fields
 * @param query Search query string
 * @returns Array of matching NigerianLocation objects
 */
export const searchLocations = (query: string): NigerianLocation[] => {
  if (!query || query.trim() === "") return [];

  const normalizedQuery = query.toLowerCase().trim();

  // Find all matching locations
  const matches = nigerianLocations.filter((location) => {
    // Check name match
    if (location.name.toLowerCase().includes(normalizedQuery)) return true;

    // Check state match (for LGAs and neighborhoods)
    if (
      location.type !== "state" &&
      location.state.toLowerCase().includes(normalizedQuery)
    )
      return true;

    // Check LGA match (for neighborhoods)
    if (
      location.type === "neighborhood" &&
      location.lga?.toLowerCase().includes(normalizedQuery)
    )
      return true;

    // Check search terms
    if (location.searchTerms?.some((term) => term.includes(normalizedQuery)))
      return true;

    return false;
  });

  // Order results: states first, then LGAs, then neighborhoods
  return matches.sort((a, b) => {
    // First sort by type
    const typeOrder = { state: 1, lga: 2, neighborhood: 3 };
    const typeDiff = typeOrder[a.type] - typeOrder[b.type];
    if (typeDiff !== 0) return typeDiff;

    // Then by popularity
    if (a.popular && !b.popular) return -1;
    if (!a.popular && b.popular) return 1;

    // Finally by how closely the name matches the query
    const aNameMatch = a.name.toLowerCase().includes(normalizedQuery) ? 1 : 0;
    const bNameMatch = b.name.toLowerCase().includes(normalizedQuery) ? 1 : 0;
    return bNameMatch - aNameMatch;
  });
};

/**
 * Get locations organized in a hierarchical structure by state and LGA
 * @returns Object with state->LGA->neighborhood hierarchy
 */
export const getLocationsByHierarchy = () => {
  const states: Record<
    string,
    {
      stateData: NigerianLocation;
      lgas: Record<
        string,
        {
          lgaData: NigerianLocation;
          neighborhoods: NigerianLocation[];
        }
      >;
    }
  > = {};

  // First pass: organize states and LGAs
  nigerianLocations.forEach((location) => {
    if (location.type === "state") {
      if (!states[location.state]) {
        states[location.state] = {
          stateData: location,
          lgas: {},
        };
      }
    } else if (location.type === "lga") {
      if (!states[location.state]) {
        // If state doesn't exist yet, create a placeholder
        states[location.state] = {
          stateData: {} as NigerianLocation, // This will be replaced if we find a state record
          lgas: {},
        };
      }

      states[location.state].lgas[location.name] = {
        lgaData: location,
        neighborhoods: [],
      };
    }
  });

  // Second pass: add neighborhoods
  nigerianLocations.forEach((location) => {
    if (location.type === "neighborhood" && location.lga) {
      if (states[location.state] && states[location.state].lgas[location.lga]) {
        states[location.state].lgas[location.lga].neighborhoods.push(location);
      }
    }
  });

  return states;
};

/**
 * Get list of location ids marked as popular for prioritized display
 * @returns Array of popular location ids
 */
export const getPopularLocations = (): string[] => {
  return nigerianLocations
    .filter((location) => location.popular)
    .map((location) => location.name);
};

/**
 * Get all state-level locations
 * @returns Array of state NigerianLocation objects
 */
export const getStates = (): NigerianLocation[] => {
  return nigerianLocations.filter((location) => location.type === "state");
};

/**
 * Get all LGAs within a specific state
 * @param state State name (e.g. "Lagos", "Oyo")
 * @returns Array of LGA NigerianLocation objects for the specified state
 */
export const getLGAsByState = (state: string): NigerianLocation[] => {
  return nigerianLocations.filter(
    (location) => location.type === "lga" && location.state === state,
  );
};

/**
 * Get all neighborhoods within a specific LGA of a state
 * @param state State name (e.g. "Lagos", "Oyo")
 * @param lga LGA name (e.g. "Eti-Osa", "Ibadan North")
 * @returns Array of neighborhood NigerianLocation objects for the specified LGA
 */
export const getNeighborhoodsByLGA = (
  state: string,
  lga: string,
): NigerianLocation[] => {
  return nigerianLocations.filter(
    (location) =>
      location.type === "neighborhood" &&
      location.state === state &&
      location.lga === lga,
  );
};

/**
 * Get coordinates for all locations organized by id
 * @returns Record mapping location ids to their coordinates
 */
export const getLocationCoordinates = (): Record<string, Coordinates> => {
  const coordinates: Record<string, Coordinates> = {};

  nigerianLocations.forEach((location) => {
    coordinates[location.name] = location.coordinates;
  });

  return coordinates;
};

/**
 * Get formatted location options for select/dropdown components
 * Organizes locations hierarchically for UI display
 * @returns Object with formatted location options
 */
export const getLocationSelectOptions = () => {
  return nigerianLocations
    .map((location) => {
      const label = location.name;
      let value = "";
      let group = "";

      switch (location.type) {
        case "state":
          value = `${location.name}, Nigeria`;
          group = "States";
          break;
        case "lga":
          value = `${location.name}, ${location.state}, Nigeria`;
          group = `${location.state} - LGAs`;
          break;
        case "neighborhood":
          value = `${location.name}, ${location.lga || ""}, ${location.state}, Nigeria`;
          group = location.lga
            ? `${location.state} - ${location.lga}`
            : location.state;
          break;
      }

      return {
        value,
        label,
        group,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Get locations with additional details for display
 * @returns Array of location objects with display details
 */
export const getLocationsWithDetails = () => {
  return nigerianLocations.map((location) => ({
    name: location.name,
    description:
      location.description ||
      `${location.name} area in ${location.state}, Nigeria.`,
    popular: location.popular,
  }));
};

/**
 * Find the closest location to provided coordinates
 * Uses the Haversine formula to calculate distances
 * @param coords Latitude/longitude coordinates to find closest location to
 * @returns ID of the closest location
 */
export const findClosestLocation = (coords: Coordinates): string => {
  let closestLocation = nigerianLocations[0].name;
  let shortestDistance = Number.MAX_VALUE;

  nigerianLocations.forEach((location) => {
    const distance = calculateDistance(coords, location.coordinates);
    if (distance < shortestDistance) {
      shortestDistance = distance;
      closestLocation = location.name;
    }
  });

  return closestLocation;
};

/**
 * Calculate distance between two coordinate points using Haversine formula
 * @param coords1 First coordinate point
 * @param coords2 Second coordinate point
 * @returns Distance in kilometers
 */
export const calculateDistance = (
  coords1: Coordinates,
  coords2: Coordinates,
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((coords2.lat - coords1.lat) * Math.PI) / 180;
  const dLng = ((coords2.lng - coords1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((coords1.lat * Math.PI) / 180) *
      Math.cos((coords2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

/**
 * Get coordinates for a given location string
 * @param locationString Location id or name
 * @returns Coordinates object or undefined if not found
 */
export const getCoordinatesForLocation = (
  locationString: string,
): Coordinates | undefined => {
  if (!locationString) return undefined;

  // Parse the location string (e.g., "Lekki Phase 1, Island, Lagos")
  const parts = locationString.split(",");
  if (parts.length === 0) return undefined;

  // The first part should be the area name
  const locationKey = parts[0].trim();

  // First attempt: Find exact match
  for (const location of nigerianLocations) {
    if (location.name.toLowerCase() === locationKey.toLowerCase()) {
      return location.coordinates;
    }
  }

  // Second attempt: Find prefix match
  for (const location of nigerianLocations) {
    if (
      locationKey.toLowerCase().startsWith(location.name.toLowerCase()) ||
      location.name.toLowerCase().startsWith(locationKey.toLowerCase())
    ) {
      return location.coordinates;
    }
  }

  // Third attempt: Find partial match
  for (const location of nigerianLocations) {
    if (
      locationKey.toLowerCase().includes(location.name.toLowerCase()) ||
      location.name.toLowerCase().includes(locationKey.toLowerCase())
    ) {
      return location.coordinates;
    }
  }

  // Final attempt: Check search terms
  for (const location of nigerianLocations) {
    if (
      location.searchTerms?.some(
        (term) =>
          term.toLowerCase().includes(locationKey.toLowerCase()) ||
          locationKey.toLowerCase().includes(term.toLowerCase()),
      )
    ) {
      return location.coordinates;
    }
  }

  return undefined;
};
